import React, { memo, useMemo, useCallback, useEffect, useRef } from 'react'
import { TableProps, Column } from './Table'

export interface OptimizedTableProps<T extends object> extends Omit<TableProps<T>, 'children'> {
  columns: Column<T>[]
  data: T[]
  emptyMessage?: string
  loading?: boolean
  className?: string
  onRowClick?: (row: T) => void
  keyExtractor?: (row: T, index: number) => string | number
}

type MemoizedRowProps<T extends object> = {
  row: T
  columns: Column<T>[]
  onRowClick?: (row: T) => void
}

const MemoizedRow = memo(
  function MemoizedRowComponent<T extends object>({ row, columns, onRowClick }: MemoizedRowProps<T>) {
    const handleClick = useCallback(() => {
      onRowClick?.(row)
    }, [onRowClick, row])

    return (
      <tr onClick={handleClick} data-testid="maintenance-row" className={onRowClick ? 'hover:bg-gray-50 cursor-pointer' : ''}>
        {columns.map(column => {
          const value = column.accessor
            ? column.accessor(row)
            : (row[column.key as keyof T] as unknown as React.ReactNode)

          return (
            <td
              key={String(column.key)}
              className={`px-6 py-4 align-top text-sm text-gray-900 ${column.className || ''}`}
            >
              {value}
            </td>
          )
        })}
      </tr>
    )
  },
  (prevProps, nextProps) =>
    prevProps.row === nextProps.row &&
    prevProps.columns === nextProps.columns &&
    prevProps.onRowClick === nextProps.onRowClick,
) as <T extends object>(props: MemoizedRowProps<T>) => React.ReactElement

const OptimizedTableComponent = <T extends object>({
  columns,
  data,
  loading = false,
  emptyMessage = 'Nenhum dado encontrado',
  className = '',
  onRowClick,
  keyExtractor = (row, index) => {
    const candidate = (row as { id?: string | number }).id
    return candidate ?? index
  },
}: OptimizedTableProps<T>): React.ReactElement | null => {
  const tableWrapperRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement | null>(null)

  useEffect(() => {
    if (!tableWrapperRef.current || !tableRef.current) {
      return
    }

    const logTableMetrics = () => {
      const wrapper = tableWrapperRef.current
      const tableElement = tableRef.current

      if (!wrapper || !tableElement) {
        return
      }

      const viewportWidth = window.innerWidth
      const wrapperClientWidth = wrapper.clientWidth
      const tableClientWidth = tableElement.clientWidth
      const tableScrollWidth = tableElement.scrollWidth
      const overflowPixels = tableScrollWidth - wrapperClientWidth

      console.debug('[OptimizedTable] layout metrics', {
        viewportWidth,
        wrapperClientWidth,
        tableClientWidth,
        tableScrollWidth,
        overflowPixels,
      })
    }

    logTableMetrics()

    window.addEventListener('resize', logTableMetrics)

    return () => {
      window.removeEventListener('resize', logTableMetrics)
    }
  }, [data, columns, loading])
  const { tableContent, shouldRender } = useMemo(() => {
    const hasData = data.length > 0

    if (!hasData && !loading) {
      return {
        tableContent: (
          <div className="text-center py-12 text-gray-500">
            {emptyMessage}
          </div>
        ),
        shouldRender: true,
      }
    }

    return {
      tableContent: (
        <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg overflow-hidden">
          <div ref={tableWrapperRef} className="overflow-x-auto -webkit-overflow-scrolling-touch" style={{ WebkitOverflowScrolling: 'touch' }}>
            <table ref={tableRef} className={`min-w-full divide-y divide-gray-300 ${className}`}>
            <thead className="bg-gray-50">
              <tr>
                {columns.map(column => (
                  <th
                    key={String(column.key)}
                    scope="col"
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.headerClassName || ''}`}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => (
                <MemoizedRow
                  key={keyExtractor(row, index)}
                  row={row}
                  columns={columns}
                  onRowClick={onRowClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      ),
      shouldRender: hasData || loading,
    }
  }, [data, columns, loading, emptyMessage, className, onRowClick, keyExtractor])

  if (!shouldRender) {
    return null
  }

  return tableContent
}

const OptimizedTable = memo(
  OptimizedTableComponent,
  (prevProps, nextProps) =>
    prevProps.data === nextProps.data &&
    prevProps.loading === nextProps.loading &&
    prevProps.emptyMessage === nextProps.emptyMessage &&
    prevProps.className === nextProps.className &&
    prevProps.onRowClick === nextProps.onRowClick &&
    JSON.stringify(prevProps.columns) === JSON.stringify(nextProps.columns),
) as <T extends object>(props: OptimizedTableProps<T>) => React.ReactElement | null

export default OptimizedTable